import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useIsOwner } from '@/auth/useSession';
import { Badge, Card, EmptyState, PageHeader, buttonVariants } from '@/components/ui';
import { useFormat } from '@/lib/format';
import { useBuyerLedgers } from './useMoney';

/** Screen 15 — Buyers list (IN-1, IN-4): balance (credit shown as "Credit"), age of the oldest unpaid amount, overdue badge. */
export function BuyersPage() {
  const { t } = useTranslation();
  const f = useFormat();
  const isOwner = useIsOwner();
  const ledgers = useBuyerLedgers();
  return (
    <div className="space-y-4">
      <PageHeader
        title={t('money.buyers')}
        actions={
          isOwner ? (
            <Link to="/buyers/new" className={buttonVariants({ size: 'sm' })}>
              <Plus className="h-4 w-4" /> {t('money.addBuyer')}
            </Link>
          ) : null
        }
      />
      {ledgers && ledgers.length === 0 ? <EmptyState>{t('money.noBuyers')}</EmptyState> : null}
      <ul className="space-y-2">
        {(ledgers ?? []).map(({ buyer, ledger }) => (
          <li key={buyer.id}>
            <Link to={`/buyers/${buyer.id}`}>
              <Card className="flex items-center gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">
                    {buyer.name} {!buyer.active ? <Badge className="ml-1">{t('common.inactive')}</Badge> : null}
                  </div>
                  <div className="text-sm text-stone-600">
                    {t('money.pricePerLiter', { price: f.birr(buyer.pricePerLiterSantim) })}
                    {ledger.oldestUnpaidAgeDays !== null ? ` · ${t('money.oldestUnpaid', { days: ledger.oldestUnpaidAgeDays })}` : ''}
                  </div>
                </div>
                <div className="text-right">
                  {ledger.balanceSantim < 0 ? (
                    <div className="font-bold text-green-800">
                      {t('money.credit')} {f.birr(-ledger.balanceSantim)}
                    </div>
                  ) : (
                    <div className="font-bold tabular-nums">{f.birr(ledger.balanceSantim)}</div>
                  )}
                  {ledger.overdueSantim > 0 ? <Badge tone="red">{t('money.overdueBadge')}</Badge> : null}
                </div>
              </Card>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
